import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import { ArrowLeft, Lock } from 'lucide-react';
import { Logo } from '@/components/layout';
import { Button, LoadingSpinner } from '@/components/ui';
import { api } from '@/lib/api';

const PLAN_NAMES: Record<string, string> = {
  analyst: 'Analyst', partner: 'Partner', team: 'Team',
  basic: 'Analyst', standard: 'Partner', professional: 'Team',
};

const TIER_PRICES: Record<string, { monthly: number; annual: number }> = {
  analyst: { monthly: 9.0, annual: 90.0 },
  partner: { monthly: 29.0, annual: 290.0 },
  team: { monthly: 79.0, annual: 790.0 },
  basic: { monthly: 9.0, annual: 90.0 },
  standard: { monthly: 29.0, annual: 290.0 },
  professional: { monthly: 79.0, annual: 790.0 },
};

// Initialize Stripe outside component to avoid re-creating
let stripePromise: ReturnType<typeof loadStripe> | null = null;

const getStripe = async () => {
  if (!stripePromise) {
    const config = await api.getStripeConfig();
    stripePromise = loadStripe(config.publishable_key);
  }
  return stripePromise;
};

export function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stripeInstance, setStripeInstance] = useState<Awaited<ReturnType<typeof loadStripe>>>(null);

  const tier = searchParams.get('tier') || 'basic';
  const period = searchParams.get('period') || 'monthly';
  const isTrial = searchParams.get('trial') === 'true';

  const planName = PLAN_NAMES[tier] || 'Plan';
  const tierPrices = TIER_PRICES[tier] || TIER_PRICES.basic;
  const price = period === 'annual' ? tierPrices.annual : tierPrices.monthly;

  useEffect(() => {
    const initCheckout = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Load Stripe
        const stripe = await getStripe();
        setStripeInstance(stripe);

        // Get the price ID based on tier and period
        const priceIdKey = `VITE_STRIPE_PRICE_${tier.toUpperCase()}_${period.toUpperCase()}`;
        const priceId = import.meta.env[priceIdKey] || `price_${tier}_${period}`;

        // Create embedded checkout session
        const result = await api.createEmbeddedCheckoutSession(priceId, isTrial);
        setClientSecret(result.client_secret);
      } catch (err) {
        console.error('Failed to initialize checkout:', err);
        setError(err instanceof Error ? err.message : 'Failed to load checkout');
      } finally {
        setIsLoading(false);
      }
    };

    initCheckout();
  }, [tier, period, isTrial]);

  const options = clientSecret ? { clientSecret } : undefined;

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <CheckoutHeader />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <p className="text-error mb-4">{error}</p>
            <Button variant="primary" onClick={() => navigate(-1)}>
              Go Back
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <CheckoutHeader />

      <main className="flex-1">
        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Compact plan summary */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
            <div>
              <h1 className="text-lg font-semibold">
                {planName} Plan
                {isTrial && <span className="ml-2 text-sm font-normal text-success">7-day free trial</span>}
              </h1>
              <p className="text-sm text-text-muted">
                ${price.toFixed(2)} {period === 'annual' ? '/year' : '/mo'}
                {isTrial && ' — billed after trial ends'}
              </p>
            </div>
            <div className="flex items-center gap-1.5 text-text-muted text-xs">
              <Lock className="w-3.5 h-3.5" />
              <span>Secure checkout</span>
            </div>
          </div>

          {/* Stripe Embedded Checkout */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden min-h-[500px]">
            {isLoading || !clientSecret || !stripeInstance ? (
              <div className="flex items-center justify-center h-full min-h-[500px]">
                <div className="text-center">
                  <LoadingSpinner size="lg" />
                  <p className="mt-4 text-text-secondary">Loading payment form...</p>
                </div>
              </div>
            ) : (
              <EmbeddedCheckoutProvider stripe={stripeInstance} options={options!}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            )}
          </div>

          {/* Terms */}
          <p className="text-xs text-text-muted text-center mt-4">
            By subscribing, you agree to our{' '}
            <Link to="/terms" className="text-primary hover:underline">Terms</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
          </p>
        </div>
      </main>
    </div>
  );
}

function CheckoutHeader() {
  return (
    <header className="border-b border-border bg-surface">
      <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
        <Link to="/dashboard/plans" className="text-text-secondary hover:text-text-primary transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <Logo size="md" linkTo="/" />
      </div>
    </header>
  );
}
